import {
  ApolloClient,
  ApolloLink,
  InMemoryCache,
  NormalizedCacheObject,
  PreloadQueryFunction,
  createQueryPreloader,
} from "@apollo/client";

let apolloClient: ApolloClient<NormalizedCacheObject> | null = null;
let preloadQuery: PreloadQueryFunction | null = null;

/**
 * A helper function to manage and retrieve a singleton instance of the Apollo Client.
 *
 * This function ensures that the Apollo Client is initialized only once and provides
 * access to the client and the `preloadQuery` function. If the client is not yet
 * initialized, you can provide an `ApolloLink` (with an `HttpLink` in its chain)
 * to initialize it.
 *
 * @param link - An optional ApolloLink used to initialize the Apollo Client if it
 *               has not already been initialized. The link must contain an `HttpLink`.
 * @returns An object containing:
 *          - `client`: The singleton instance of the Apollo Client.
 *          - `preloadQuery`: A singleton function to preload queries using the Apollo Client.
 * @throws An error if:
 *         - The function is called without a `link` and the client has not been initialized.
 *         - The `preloadQuery` function is accessed before the client is initialized.
 */
export const getApollo = (
  link?: ApolloLink,
): {
  client: ApolloClient<NormalizedCacheObject>;
  preloadQuery: PreloadQueryFunction;
} => {
  if (!apolloClient && link) {
    apolloClient = new ApolloClient({
      link: link,
      cache: new InMemoryCache({
        addTypename: true,
        typePolicies: {
          SearchQueries: { merge: true },
          ChannelsQueries: { merge: true },
          PlaylistsQueries: { merge: true },
          CuratedPlaylistQueries: { merge: true },
          TracksQueries: { merge: true },
          TrackUserMeta: { merge: true },
          PayoutQueries: { merge: true },
          StatementImportQueries: { merge: true },
          StatementPayment: { merge: true },
          StatementReport: { merge: true },
          StatementReportFile: { merge: true },
          StatementDetail: { merge: true },
          SignatureDocument: { keyFields: ["documentId"] },
          MsaProfile: { keyFields: ["key"] },
          PayoutEligibleOrg: { keyFields: ["orgId"] },
          Query: {
            fields: {
              recentSearches: {
                merge: false, // always replace the array, never try to merge
              },
              track: {
                read(_, { args, toReference, canRead }) {
                  const ref = toReference({
                    __typename: "Track",
                    id: args?.id,
                  });
                  return canRead(ref) ? ref : undefined;
                },
              },
            },
          },
        },
      }),
    });

    preloadQuery = createQueryPreloader(apolloClient);

    return { client: apolloClient, preloadQuery };
  } else if (!apolloClient)
    throw new Error(
      "The Apollo client has not been initialized. Please provide an ApolloLink (that has an HttpLink in its link chain) as an argument to this function.",
    );

  if (!preloadQuery) {
    throw new Error(
      "The preloadQuery function is not available until the Apollo client has been initialized",
    );
  }

  return { client: apolloClient, preloadQuery };
};
